import { IconButton, notify } from '@affine/component';
import { MeetingsService } from '@affine/core/modules/meetings';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { PlusIcon, TodayIcon } from '@blocksuite/icons/rc';
import { useLiveData, useServices } from '@toeverything/infra';
import { useCallback, useMemo, useState } from 'react';

import { CollapsibleSection } from '../../layouts/collapsible-section';
import { NavigationPanelDocNode } from '../../nodes/doc';
import { NavigationPanelTreeRoot } from '../../tree';
import { RootEmpty } from './empty';

/**
 * Meeting Notes section in the navigation panel.
 * - Click section title: Opens the Meeting Notes database (table view)
 * - Click +: Creates new meeting from template, opens it for editing
 */
export const NavigationPanelMeetings = () => {
  const { meetingsService, workbenchService } = useServices({
    MeetingsService,
    WorkbenchService,
  });

  const workbench = workbenchService.workbench;
  const meetingsDocs = useLiveData(meetingsService.meetingsDocs$);
  const path = useMemo(() => ['meeting-notes'], []);
  const [creating, setCreating] = useState(false);

  // Handle clicking the section title - opens the Meeting Notes database
  const handleOpenMeetingNotesDb = useCallback(async () => {
    await meetingsService.waitForInit();
    const dbId = meetingsService.getMeetingNotesDbId();
    if (dbId) {
      workbench.openDoc(dbId);
    }
  }, [meetingsService, workbench]);

  // Handle clicking + - creates new meeting from template
  const handleCreateMeeting = useCallback(async () => {
    if (creating) return;
    setCreating(true);
    try {
      const newDocId = await meetingsService.createNewMeeting();
      workbench.openDoc(newDocId);
    } catch (error) {
      console.error('[NavigationPanelMeetings] Error creating meeting:', error);
      notify.error({
        title: 'Failed to create meeting',
        message:
          error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setCreating(false);
    }
  }, [meetingsService, workbench, creating]);

  return (
    <CollapsibleSection
      path={path}
      testId="navigation-panel-meeting-notes"
      title="Meeting Notes"
      onLabelClick={handleOpenMeetingNotesDb}
      actions={
        <IconButton
          data-testid="navigation-panel-bar-add-meeting-button"
          onClick={handleCreateMeeting}
          size="16"
          tooltip={creating ? 'Creating...' : 'New Meeting'}
          disabled={creating}
        >
          <PlusIcon />
        </IconButton>
      }
    >
      <NavigationPanelTreeRoot
        placeholder={<RootEmpty onClickCreate={handleCreateMeeting} />}
      >
        {meetingsDocs.map(doc => (
          <NavigationPanelDocNode
            key={doc.id}
            docId={doc.id}
            icon={TodayIcon}
            location={{
              at: 'navigation-panel:meeting-notes:list',
            }}
            parentPath={path}
          />
        ))}
      </NavigationPanelTreeRoot>
    </CollapsibleSection>
  );
};
